import React from 'react';
import { 
  LocalShipping, 
  Timeline, 
  LocationOn, 
  CheckCircle, 
  Warning, 
  Help,
  ContactSupport,
  Payment,
  TrackChanges,
  Store,
  LocalOffer,
  Info,
  ArrowForward
} from '@mui/icons-material';

const ShippingPolicyPage: React.FC = () => {
  const shippingRates = {
    cairo: 60,
    giza: 70,
    alexandria: 80,
    otherCities: 90,
    remoteAreas: 120
  };

  const deliveryTimes = {
    cairo: '1-2 أيام عمل',
    giza: '1-3 أيام عمل',
    alexandria: '2-4 أيام عمل',
    otherCities: '3-5 أيام عمل',
    remoteAreas: '5-7 أيام عمل'
  };

  const freeShippingThreshold = 1000; // مجاني فوق 1000 جنيه

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" dir="rtl">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        
        {/* Header Section */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-gradient-to-r from-blue-500 to-indigo-500 p-5 rounded-2xl shadow-xl">
                <LocalShipping className="text-white text-4xl" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            سياسة الشحن والتوصيل
          </h1>
          
          <div className="h-1.5 w-32 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto rounded-full mb-4"></div>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            تكاليف الشحن تبدأ من <span className="font-bold text-blue-600">60 جنيه</span> - تواصل مع المتجر لمعرفة التفاصيل الدقيقة
          </p>
        </div>

        {/* Free Shipping Banner */}
        <div className="mb-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <LocalOffer className="text-white text-2xl" />
              <div>
                <h3 className="text-xl font-bold">توصيل مجاني!</h3>
                <p className="text-green-100">
                  للطلبات فوق {freeShippingThreshold} جنيه في القاهرة والجيزة
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{freeShippingThreshold} جنيه+</p>
              <p className="text-sm text-green-100">قيمة الحد الأدنى</p>
            </div>
          </div>
        </div>

        {/* Shipping Rates Table */}
        <div className="mb-8 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <Payment className="text-blue-600" />
              جدول تكاليف الشحن
            </h3>
            <p className="text-gray-600">الأسعار بالجنيه المصري - تبدأ من 60 جنيه</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-4 px-4 text-right font-semibold text-gray-900">
                    <div className="flex items-center gap-2">
                      <LocationOn className="text-gray-500" />
                      <span>المنطقة</span>
                    </div>
                  </th>
                  <th className="py-4 px-4 text-center font-semibold text-gray-900">
                    <div className="flex items-center justify-center gap-2">
                      <LocalShipping className="text-gray-500" />
                      <span>تكلفة الشحن</span>
                    </div>
                  </th>
                  <th className="py-4 px-4 text-center font-semibold text-gray-900">
                    <div className="flex items-center justify-center gap-2">
                      <Timeline className="text-gray-500" />
                      <span>مدة التوصيل</span>
                    </div>
                  </th>
                  <th className="py-4 px-4 text-center font-semibold text-gray-900">
                    <div className="flex items-center justify-center gap-2">
                      <LocalOffer className="text-gray-500" />
                      <span>توصيل مجاني فوق</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* القاهرة */}
                <tr className="hover:bg-blue-50/30 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <div>
                        <span className="font-semibold text-gray-900 block">القاهرة</span>
                        <span className="text-sm text-gray-500">جميع مناطق القاهرة</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div>
                      <span className="text-2xl font-bold text-blue-600">
                        {shippingRates.cairo} جنيه
                      </span>
                      <p className="text-sm text-gray-500 mt-1">الحد الأدنى للشحن</p>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
                      <Timeline className="text-blue-500 text-sm" />
                      <span className="font-medium text-blue-700">
                        {deliveryTimes.cairo}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full">
                      <LocalOffer className="text-green-500 text-sm" />
                      <span className="font-medium text-green-700">
                        {freeShippingThreshold} جنيه
                      </span>
                    </div>
                  </td>
                </tr>

                {/* الجيزة */}
                <tr className="hover:bg-blue-50/30 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                      <div>
                        <span className="font-semibold text-gray-900 block">الجيزة</span>
                        <span className="text-sm text-gray-500">6 أكتوبر، الشيخ زايد</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div>
                      <span className="text-2xl font-bold text-indigo-600">
                        {shippingRates.giza} جنيه
                      </span>
                      <p className="text-sm text-gray-500 mt-1">+10 زيادة عن القاهرة</p>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-indigo-50 px-3 py-1 rounded-full">
                      <Timeline className="text-indigo-500 text-sm" />
                      <span className="font-medium text-indigo-700">
                        {deliveryTimes.giza}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full">
                      <LocalOffer className="text-green-500 text-sm" />
                      <span className="font-medium text-green-700">
                        {freeShippingThreshold} جنيه
                      </span>
                    </div>
                  </td>
                </tr>

                {/* الإسكندرية */}
                <tr className="hover:bg-blue-50/30 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      <div>
                        <span className="font-semibold text-gray-900 block">الإسكندرية</span>
                        <span className="text-sm text-gray-500">جميع مناطق الإسكندرية</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div>
                      <span className="text-2xl font-bold text-purple-600">
                        {shippingRates.alexandria} جنيه
                      </span>
                      <p className="text-sm text-gray-500 mt-1">+20 زيادة عن القاهرة</p>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                      <Timeline className="text-purple-500 text-sm" />
                      <span className="font-medium text-purple-700">
                        {deliveryTimes.alexandria}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-amber-50 px-3 py-1 rounded-full">
                      <LocalOffer className="text-amber-500 text-sm" />
                      <span className="font-medium text-amber-700">
                        1500 جنيه
                      </span>
                    </div>
                  </td>
                </tr>

                {/* المحافظات الأخرى */}
                <tr className="hover:bg-blue-50/30 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
                      <div>
                        <span className="font-semibold text-gray-900 block">المحافظات الأخرى</span>
                        <span className="text-sm text-gray-500">المنصورة، طنطا، الزقازيق</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div>
                      <span className="text-2xl font-bold text-cyan-600">
                        {shippingRates.otherCities} جنيه
                      </span>
                      <p className="text-sm text-gray-500 mt-1">+30 زيادة عن القاهرة</p>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-cyan-50 px-3 py-1 rounded-full">
                      <Timeline className="text-cyan-500 text-sm" />
                      <span className="font-medium text-cyan-700">
                        {deliveryTimes.otherCities}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-amber-50 px-3 py-1 rounded-full">
                      <LocalOffer className="text-amber-500 text-sm" />
                      <span className="font-medium text-amber-700">
                        2000 جنيه
                      </span>
                    </div>
                  </td>
                </tr>

                {/* المناطق النائية */}
                <tr className="hover:bg-blue-50/30 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <div>
                        <span className="font-semibold text-gray-900 block">المناطق النائية</span>
                        <span className="text-sm text-gray-500">الواحات، جنوب سيناء</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div>
                      <span className="text-2xl font-bold text-orange-600">
                        {shippingRates.remoteAreas} جنيه
                      </span>
                      <p className="text-sm text-gray-500 mt-1">+60 زيادة عن القاهرة</p>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-orange-50 px-3 py-1 rounded-full">
                      <Timeline className="text-orange-500 text-sm" />
                      <span className="font-medium text-orange-700">
                        {deliveryTimes.remoteAreas}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-amber-50 px-3 py-1 rounded-full">
                      <LocalOffer className="text-amber-500 text-sm" />
                      <span className="font-medium text-amber-700">
                        2500 جنيه
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Important Notes */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <Info className="text-blue-600 text-xl" />
              <h3 className="font-bold text-gray-900">ملاحظات هامة</h3>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 text-sm mt-1" />
                <span className="text-gray-700">الأسعار تشمل ضريبة القيمة المضافة</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 text-sm mt-1" />
                <span className="text-gray-700">يتم احتساب تكلفة الشحن قبل إتمام الطلب</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 text-sm mt-1" />
                <span className="text-gray-700">لا توجد رسوم خفية أو مفاجئة</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 text-sm mt-1" />
                <span className="text-gray-700">يمكن دمج طلبات متعددة من نفس المتجر</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
            <div className="flex items-center gap-3 mb-4">
              <LocalOffer className="text-green-600 text-xl" />
              <h3 className="font-bold text-gray-900">عروض الشحن</h3>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 text-sm mt-1" />
                <span className="text-gray-700">توصيل مجاني للطلبات الأولى من كل عميل جديد</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 text-sm mt-1" />
                <span className="text-gray-700">خصم 50% على الشحن للطلبات المتكررة</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 text-sm mt-1" />
                <span className="text-gray-700">عروض شحن خاصة في الأعياد والمناسبات</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 text-sm mt-1" />
                <span className="text-gray-700">برنامج الولاء: شحن مجاني بعد 10 طلبات</span>
              </li>
            </ul>
          </div>
        </div>

        {/* How to Calculate */}
        <div className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Help className="text-blue-600" />
            كيف يتم احتساب تكلفة الشحن؟
          </h3>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">الخطوة 1: اختيار المنطقة</h4>
              <p className="text-gray-700">يتم تحديد تكلفة الشحن الأساسية حسب المنطقة (تبدأ من 60 جنيه للقاهرة)</p>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">الخطوة 2: إضافة الوزن والحجم</h4>
              <p className="text-gray-700">تتم إضافة رسوم إضافية للمنتجات الثقيلة أو كبيرة الحجم</p>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-2">الخطوة 3: الخصومات والعروض</h4>
              <p className="text-gray-700">يتم تطبيق عروض الشحن المجاني أو الخصومات إذا كانت متاحة</p>
            </div>
            
            <div className="p-4 bg-amber-50 rounded-lg">
              <h4 className="font-semibold text-amber-800 mb-2">الخطوة 4: التكلفة النهائية</h4>
              <p className="text-gray-700">تظهر التكلفة النهائية للشحن قبل تأكيد عملية الشراء</p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ContactSupport className="text-white text-3xl" />
            <h3 className="text-2xl font-bold">
              استفسارات الشحن والتوصيل
            </h3>
          </div>
          
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto text-center">
            لديك سؤال عن تكاليف الشحن؟ فريق الدعم جاهز للإجابة على جميع استفساراتك
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:shipping@bazaar-marketplace.com"
              className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300"
            >
              <span className="flex items-center gap-2">
                <span>البريد الإلكتروني</span>
              </span>
            </a>
            <a
              href="tel:+201503323006"
              className="px-6 py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-colors duration-300"
            >
              <span className="flex items-center gap-2">
                <span>اتصال مباشر</span>
              </span>
            </a>
            <a
              href="https://wa.me/201503323006"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors duration-300"
            >
              <span className="flex items-center gap-2">
                <span>واتساب</span>
              </span>
            </a>
          </div>
          
          <div className="mt-6 text-center text-blue-100 text-sm">
            <p>ساعات العمل: 10 صباحًا - 10 مساءً (بتوقيت مصر)</p>
            <p className="mt-1">متوسط وقت الرد: خلال ساعتين</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-3 rounded-full">
              <LocalShipping className="text-blue-600" />
              <span className="text-blue-700 font-bold">
                شحن سريع وآمن - تبدأ التكلفة من 60 جنيه
              </span>
            </div>
          </div>
          
          <div className="text-gray-600 text-sm leading-relaxed max-w-2xl mx-auto space-y-2">
            <p>
              نحن نضمن وصول طلباتك بأسرع وقت ممكن وبأعلى معايير الجودة والأمان
            </p>
            <p className="text-gray-500">
              آخر تحديث للسياسة: {new Date().toLocaleDateString('ar-EG')}
            </p>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:opacity-90 transition-opacity duration-300"
            >
              العودة للأعلى
            </button>
            <a
              href="/contact"
              className="px-6 py-2 bg-white border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white transition-all duration-300"
            >
              تواصل معنا
            </a>
            <a
              href="/faq"
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300"
            >
              الأسئلة الشائعة
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicyPage;